import { useSortableTable } from './hooks/useSortableTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from './utils/currency';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function Consumables() {
  const navigate = useNavigate();
  const [consumables, setConsumables] = useState([]);
  
  // Sorting
  const { items: sortedConsumables, requestSort, getSortIndicator } = useSortableTable(consumables, { key: 'product_name', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    productCode: true,
    productName: true,
    type: true,
    category: true,
    costPrice: true,
    price: true,
    markup: true,
    stock: true,
    location: true,
    barcode: false,
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  useEffect(() => {
    fetchConsumables();
    loadColumnPreferences();
  }, []);

  const fetchConsumables = async () => {
    try {
      const response = await apiFetch('/api/products');
      const data = await response.json();
      // Filter to only Consumable type
      setConsumables(data.filter(p => p.type === 'Consumable'));
    } catch (error) {
      console.error('Error fetching consumables:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadColumnPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/consumables');
      const data = await response.json();
      if (data.preferences?.visibleColumns) {
        setVisibleColumns(data.preferences.visibleColumns);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const saveColumnPreferences = async (columns) => {
    try {
      await apiFetch('/api/preferences/default/consumables', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: { visibleColumns: columns } })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const toggleColumn = (column) => {
    const updated = { ...visibleColumns, [column]: !visibleColumns[column] };
    setVisibleColumns(updated);
    saveColumnPreferences(updated);
  };

  const filteredConsumables = sortedConsumables.filter(product => {
    const matchesSearch = 
      product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.barcode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || product.type === filterType;
    const matchesCategory = filterCategory === 'all' || product.product_category === filterCategory;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  // Group by category
  const groupedConsumables = filteredConsumables.reduce((acc, product) => {
    const category = product.product_category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {});

  const categories = Object.keys(groupedConsumables).sort();

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleAllCategories = (expand) => {
    const newState = {};
    categories.forEach(cat => {
      newState[cat] = expand;
    });
    setExpandedCategories(newState);
  };

  if (loading) {
    return <div className="p-8 text-center">Loading consumables...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Consumables"
        subtitle={`${consumables.length} total consumables`}
        backButton
        onBack={() => navigate('/stock')}
        backTitle="Back to Stock"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/consumables/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Add a Consumable
          </button>
        }
      />

      <div className="px-4 py-3">
        {/* Filters */}
        <div className="mb-3 flex items-center gap-2">
          {/* Search */}
          <input
            type="text"
            placeholder="Search by name, code, or barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="Product">Product</option>
            <option value="Service">Service</option>
            <option value="Consumable">Consumable</option>
          </select>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            {[...new Set(consumables.map(p => p.product_category).filter(Boolean))].map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Column Selector */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="px-3 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg text-sm font-medium whitespace-nowrap"
            >
              ⚙️ Columns
            </button>
            
            {showColumnSelector && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="p-3 space-y-2">
                  {Object.keys(visibleColumns).map(column => (
                    <label key={column} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns[column]}
                        onChange={() => toggleColumn(column)}
                        className="rounded text-blue-600"
                      />
                      <span className="text-sm capitalize">
                        {column.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expand/Collapse All */}
        {!loading && filteredConsumables.length > 0 && (
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => toggleAllCategories(true)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
            >
              ▼ Expand All
            </button>
            <button
              onClick={() => toggleAllCategories(false)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
            >
              ▶ Collapse All
            </button>
            <span className="text-sm text-gray-500 ml-2 self-center">
              {categories.length} categories • {filteredConsumables.length} consumables
            </span>
          </div>
        )}

        {/* Grouped Consumables */}
        {filteredConsumables.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No consumables found</h3>
            <p className="text-gray-500">
              {searchTerm || filterType !== 'all' || filterCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'No consumables yet. Click "Add a Consumable" to get started.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map(category => (
              <div key={category} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 flex items-center justify-between text-left transition"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-gray-600 font-mono text-sm">
                      {expandedCategories[category] ? '▼' : '▶'}
                    </span>
                    <h3 className="font-semibold text-gray-800">{category}</h3>
                    <span className="text-sm text-gray-500">
                      ({groupedConsumables[category].length} consumables)
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Total Stock: {groupedConsumables[category].reduce((sum, p) => sum + parseFloat(p.current_stock || 0), 0).toFixed(0)}
                  </div>
                </button>

                {/* Category Consumables Table */}
                {expandedCategories[category] && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {visibleColumns.productCode && (
                            <th 
                              onClick={() => requestSort('product_code')}
                              className="text-left px-4 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                            >
                              Code {getSortIndicator('product_code')}
                            </th>
                          )}
                          {visibleColumns.productName && (
                            <th 
                              onClick={() => requestSort('product_name')}
                              className="text-left px-4 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                            >
                              Name {getSortIndicator('product_name')}
                            </th>
                          )}
                          {visibleColumns.type && (
                            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">
                              Type
                            </th>
                          )}
                          {visibleColumns.category && (
                            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">
                              Category
                            </th>
                          )}
                          {visibleColumns.costPrice && (
                            <th 
                              onClick={() => requestSort('cost_price')}
                              className="text-right px-4 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                            >
                              Cost {getSortIndicator('cost_price')}
                            </th>
                          )}
                          {visibleColumns.price && (
                            <th 
                              onClick={() => requestSort('price')}
                              className="text-right px-4 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                            >
                              Price {getSortIndicator('price')}
                            </th>
                          )}
                          {visibleColumns.markup && (
                            <th 
                              onClick={() => requestSort('markup_percent')}
                              className="text-right px-4 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                            >
                              Markup {getSortIndicator('markup_percent')}
                            </th>
                          )}
                          {visibleColumns.stock && (
                            <th 
                              onClick={() => requestSort('current_stock')}
                              className="text-right px-4 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                            >
                              Stock {getSortIndicator('current_stock')}
                            </th>
                          )}
                          {visibleColumns.location && (
                            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">
                              Location
                            </th>
                          )}
                          {visibleColumns.barcode && (
                            <th className="text-left px-4 py-2 text-xs font-semibold text-gray-600">
                              Barcode
                            </th>
                          )}
                          <th className="text-right px-4 py-2 text-xs font-semibold text-gray-600">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {groupedConsumables[category].map((product, index) => (
                          <tr
                            key={product.id}
                            onClick={() => navigate(`/consumables/${product.id}`)}
                            className={`hover:bg-blue-50 cursor-pointer transition ${
                              index % 2 === 1 ? 'bg-gray-50' : 'bg-white'
                            }`}
                          >
                            {visibleColumns.productCode && (
                              <td className="px-4 py-2 text-sm font-medium text-gray-700">
                                {product.product_code}
                              </td>
                            )}
                            {visibleColumns.productName && (
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {product.product_name}
                              </td>
                            )}
                            {visibleColumns.type && (
                              <td className="px-4 py-2 text-sm text-gray-700">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  product.type === 'Product' ? 'bg-blue-100 text-blue-700' :
                                  product.type === 'Service' ? 'bg-purple-100 text-purple-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {product.type}
                                </span>
                              </td>
                            )}
                            {visibleColumns.category && (
                              <td className="px-4 py-2 text-sm text-gray-700">
                                {product.product_category || '-'}
                              </td>
                            )}
                            {visibleColumns.costPrice && (
                              <td className="px-4 py-2 text-sm text-right text-gray-700">
                                {formatCurrency(product.cost_price)}
                              </td>
                            )}
                            {visibleColumns.price && (
                              <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                                {formatCurrency(product.price)}
                              </td>
                            )}
                            {visibleColumns.markup && (
                              <td className="px-4 py-2 text-sm text-right text-gray-700">
                                {product.markup ? `${parseFloat(product.markup).toFixed(1)}%` : '-'}
                              </td>
                            )}
                            {visibleColumns.stock && (
                              <td className="px-4 py-2 text-sm text-right">
                                <span className={`font-medium ${
                                  parseFloat(product.current_stock || 0) <= 0 ? 'text-red-600' :
                                  parseFloat(product.current_stock || 0) < product.reorder_level ? 'text-orange-600' :
                                  'text-green-600'
                                }`}>
                                  {parseFloat(product.current_stock || 0).toFixed(0)}
                                </span>
                              </td>
                            )}
                            {visibleColumns.location && (
                              <td className="px-4 py-2 text-sm text-gray-700">
                                {product.location || '-'}
                              </td>
                            )}
                            {visibleColumns.barcode && (
                              <td className="px-4 py-2 text-sm text-gray-600">
                                {product.barcode || '-'}
                              </td>
                            )}
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/consumables/${product.id}`);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Consumables;
